import { Controller } from "@hotwired/stimulus"

/**
 * Notification Toast Controller
 * 
 * Handles individual toast notifications with auto-dismiss,
 * progress bar, and action handling.
 */
export default class extends Controller {
  static targets = ["progress"]
  static values = {
    autoDismiss: { type: Boolean, default: true },
    dismissDelay: { type: Number, default: 5000 }
  }

  connect() {
    if (this.autoDismissValue) {
      this.startAutoDismiss()
    }
    
    // Pause on hover
    this.element.addEventListener("mouseenter", this.pause.bind(this))
    this.element.addEventListener("mouseleave", this.resume.bind(this))
  }

  disconnect() {
    this.clearTimers()
  }

  startAutoDismiss() {
    this.dismissTimer = setTimeout(() => {
      this.dismiss()
    }, this.dismissDelayValue)
    
    // Reset progress bar animation
    if (this.hasProgressTarget) {
      this.progressTarget.style.animationDuration = `${this.dismissDelayValue}ms`
      this.progressTarget.style.animationPlayState = "running"
    }
  }

  pause() {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer)
      this.dismissTimer = null
    }
    
    if (this.hasProgressTarget) {
      this.progressTarget.style.animationPlayState = "paused"
    }
  }

  resume() {
    if (this.autoDismissValue && !this.dismissTimer) {
      // Calculate remaining time based on progress bar width
      const remaining = this.hasProgressTarget
        ? parseFloat(getComputedStyle(this.progressTarget).width) / parseFloat(getComputedStyle(this.element).width) * this.dismissDelayValue
        : this.dismissDelayValue / 2
      
      this.dismissTimer = setTimeout(() => {
        this.dismiss()
      }, remaining)
      
      if (this.hasProgressTarget) {
        this.progressTarget.style.animationPlayState = "running"
      }
    }
  }

  dismiss() {
    this.clearTimers()
    
    // Animate out
    this.element.classList.add("notification-toast--dismissing")
    
    setTimeout(() => {
      this.element.remove()
    }, 250)
  }

  handleAction(event) {
    const actionUrl = event.currentTarget.dataset.actionUrl
    
    if (actionUrl) {
      // Dismiss immediately
      this.dismiss()
      
      // Navigate or dispatch event
      if (actionUrl.startsWith("/") || actionUrl.startsWith("http")) {
        window.location.href = actionUrl
      } else {
        // Dispatch custom event for parent controllers
        this.dispatch("action", { detail: { url: actionUrl } })
      }
    }
  }

  clearTimers() {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer)
      this.dismissTimer = null
    }
  }
}
